import { config } from "@config";
import { inject, Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { google } from "google-maps";
import { GooglePlaceAddress } from "@models/google_place_address";
import { COUNTRY_RESTRICTIONS } from "@helpers/constants";
import { divideArrayInChunks } from "@helpers/utils";
import { ScriptManagerService } from "@core-services/script-manager.service";
import { Script } from "@models/script";

declare var google: google;

@Injectable({
	providedIn: "root"
})
export class GooglePlacesService {
	public searchFinished: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public predictionResultsSubject: BehaviorSubject<GooglePlaceAddress[]> = new BehaviorSubject<GooglePlaceAddress[]>(
		[]
	);

	private readonly scriptManagerService = inject(ScriptManagerService);

	public async getPlacePredictions(
		inputValue: string,
		countryRestrictions: string[],
		sessionToken: google.maps.places.AutocompleteSessionToken
	): Promise<google.maps.places.AutocompletePrediction[]> {
		await this.ensurePlacesScriptLoaded();

		const countryChunks = divideArrayInChunks(countryRestrictions, COUNTRY_RESTRICTIONS);
		const service = new google.maps.places.AutocompleteService();

		const predictionTasks = countryChunks.map(async (chunk) => {
			const request = {
				input: inputValue,
				componentRestrictions: { country: chunk },
				sessionToken: sessionToken
			};
			try {
				const predictions = await this.getPredictions(service, request);
				return predictions;
			} catch (error) {
				console.error("Error getting place predictions:", error);
				return [];
			}
		});

		try {
			const results = await Promise.all(predictionTasks);
			const combinedResults = [].concat(...results);
			return combinedResults;
		} finally {
			this.searchFinished.next(true);
		}
	}

	public async getCityPredictions(
		inputValue: string,
		sessionToken: google.maps.places.AutocompleteSessionToken
	): Promise<google.maps.places.AutocompletePrediction[]> {
		await this.ensurePlacesScriptLoaded();

		const service = new google.maps.places.AutocompleteService();

		const request = {
			input: inputValue,
			sessionToken: sessionToken,
			types: ["(cities)"]
		};

		try {
			const predictions = await this.getPredictionsCities(service, request);
			console.warn("predictions facade: ", predictions);
			return predictions;
		} catch (error) {
			console.warn(error);
			return [];
		} finally {
			this.searchFinished.next(true);
		}
	}

	public async getPlaceDetails(
		placeId: string,
		sessionToken: google.maps.places.AutocompleteSessionToken
	): Promise<GooglePlaceAddress> {
		await this.ensurePlacesScriptLoaded();

		return new Promise<GooglePlaceAddress>((resolve, reject) => {
			const service: google.maps.places.PlacesService = new google.maps.places.PlacesService(
				document.createElement("div")
			);
			service.getDetails({ placeId, sessionToken }, (place, status) => {
				if (status === google.maps.places.PlacesServiceStatus.OK && place) {
					const address: GooglePlaceAddress = new GooglePlaceAddress();
					for (const component of place.address_components) {
						const componentType = component.types[0];
						switch (componentType) {
							case "street_number":
								address.setStreet(address.getStreet() + " " + component.long_name);
								address.setStreetNumber(component.long_name);
								break;
							case "route":
								address.setStreet(component.short_name + " " + address.getStreet());
								break;
							case "postal_code":
								address.setPostCode(component.long_name);
								break;
							case "postal_code_suffix":
								address.setPostCode(address.getPostCode() + "-" + component.long_name);
								break;
							case "locality":
								address.setCity(component.long_name);
								break;
							case "administrative_area_level_1":
								address.setState(component.short_name);
								break;
							case "country":
								address.setCountry(component.long_name);
								break;
							default:
								break;
						}
					}
					resolve(address);
				} else {
					reject(status);
				}
			});
		});
	}

	/* eslint-disable  @typescript-eslint/no-explicit-any */
	private getPredictions(
		service: google.maps.places.AutocompleteService,
		request: google.maps.places.AutocompletionRequest
	): Promise<google.maps.places.AutocompletePrediction[]> {
		return new Promise((resolve, reject) => {
			service.getPlacePredictions(request, (predictions, status) => {
				if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
					const addressTypes = ["route", "street_address", "geocode"];
					const addressPredictions = predictions.filter((prediction) =>
						prediction.types.some((type) => addressTypes.includes(type))
					);
					resolve(addressPredictions);
				} else {
					reject(status);
				}
			});
		});
	}

	private getPredictionsCities(
		service: google.maps.places.AutocompleteService,
		request: google.maps.places.AutocompletionRequest
	): Promise<google.maps.places.AutocompletePrediction[]> {
		return new Promise((resolve, reject) => {
			service.getPlacePredictions(request, (predictions, status) => {
				if (status === google.maps.places.PlacesServiceStatus.OK) {
					if (!predictions) {
						resolve([]);
					}
					resolve(predictions);
				} else {
					reject(status);
				}
			});
		});
	}

	public predictionsCallback(resultList: GooglePlaceAddress[]): GooglePlaceAddress[] {
		const uniqueCombinations = new Set<string>();

		// Filter the resultList to remove duplicates
		const filteredResults = resultList.filter((address) => {
			const uniqueKey = `${address.getStreet()}|${address.getCity()}|${address.getStreetNumber()}`;

			if (!uniqueCombinations.has(uniqueKey)) {
				uniqueCombinations.add(uniqueKey);
				return true; // Keep this item as it's not a duplicate
			}
			// If the unique identifier is already in the Set, it's a duplicate
			return false;
		});

		// Sort by the length of the street description
		filteredResults.sort((p1, p2) => p1.getStreet().length - p2.getStreet().length);

		return filteredResults;
	}

	public async getSessionToken(
		existingToken?: google.maps.places.AutocompleteSessionToken
	): Promise<google.maps.places.AutocompleteSessionToken> {
		await this.ensurePlacesScriptLoaded();
		return existingToken ?? new google.maps.places.AutocompleteSessionToken();
	}

	private async ensurePlacesScriptLoaded(): Promise<void> {
		const script = new Script("googlePlacesSuggestion");
		script.src = config.google["mapsPlacesApi"];
		await this.scriptManagerService.loadScript(script);
	}
}
